// The SHARED model map (brief 2026-10-05, Addendum 5; docs/lead-relay.md §25).
// Pure.
//
// ⚠️ THE MAP IS NOT A PROPERTY OF A STORE. What is per-store is `lid`,
// `accountId` and `formUrl`; the model list is a property of VirtualYard + BYD.
// Twenty BYD dealerships holding twenty hand-typed copies of one list means the
// day BYD adds a model, the routes nobody remembered to update send those leads
// to General Enquiry — visible only as an interestFallback badge, and only if a
// person looks.
//
// ⭐⭐ STORE THE DIFF, NOT THE COPY. A route references a standard and holds
// ONLY the rows where it departs from it (`ModelMapOverrides`): a value that
// differs, an answer the standard lacks, or `null` for a standard row this route
// removes. So when a model is added to the standard, a route that overrode one
// unrelated row STILL RECEIVES IT. A whole-map copy would silently freeze that
// route at the day it was customised — the failure this exists to prevent, and
// one no test of today's behaviour would notice.
//
// ⚠️ CASE-INSENSITIVE: every key is normalised (normalizeKey) wherever it enters.
//
// ⚠️⚠️ AN OVERRIDE YOU CANNOT SEE IS THE BUG. `describeModelMapRows` labels
// every row with where it came from and, for an override, what the standard
// would have given — the editor renders exactly that, and the routes list shows
// how many rows each route overrides.

import { normalizeKey } from './normalize.js';

/** Answer (normalised) → VirtualYard option, or `null` = "this standard row is removed on this route". */
export type ModelMapOverrides = Record<string, string | null>;

/** The one standard today. Its id is the collection doc id (`leadModelMaps/virtualyard-byd`). */
export const BYD_MODEL_MAP_STANDARD_ID = 'virtualyard-byd';

export interface ModelMapStandard {
  id: string;
  /** "BYD standard" — what the editor calls an inherited row's source. */
  label: string;
  /** Normalised answer → option. */
  map: Record<string, string>;
}

function normalized<T>(record: Record<string, T>): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) {
    const k = normalizeKey(key);
    if (k !== '') out[k] = value;
  }
  return out;
}

/** The map a route actually composes with: the standard, with this route's overrides applied. */
export function effectiveModelMap(
  standard: Record<string, string> | null,
  overrides: ModelMapOverrides,
): Record<string, string> {
  const out: Record<string, string> = { ...normalized(standard ?? {}) };
  for (const [answer, value] of Object.entries(normalized(overrides))) {
    if (value === null) delete out[answer];
    else out[answer] = value;
  }
  return out;
}

/**
 * The overrides that turn `standard` into `effective` — ONLY the departures.
 * A row equal to the standard is not an override, so a route that matches the
 * standard holds `{}` and inherits every future change to it.
 */
export function overridesAgainst(
  standard: Record<string, string> | null,
  effective: Record<string, string>,
): ModelMapOverrides {
  const base = normalized(standard ?? {});
  const next = normalized(effective);
  const out: ModelMapOverrides = {};
  for (const [answer, value] of Object.entries(next)) {
    if (base[answer] !== value) out[answer] = value;
  }
  for (const answer of Object.keys(base)) {
    if (!(answer in next)) out[answer] = null;
  }
  return out;
}

/**
 * ⭐ The write-boundary guard for STORE THE DIFF: drop every "override" that is
 * not one — a value equal to the standard's, or a `null` removing a row the
 * standard does not have. A caller that sends the whole standard back as
 * overrides therefore stores `{}`, not a copy that would freeze the route.
 */
export function pruneOverrides(
  standard: Record<string, string> | null,
  overrides: ModelMapOverrides,
): ModelMapOverrides {
  const base = normalized(standard ?? {});
  const out: ModelMapOverrides = {};
  for (const [answer, value] of Object.entries(normalized(overrides))) {
    if (value === null ? answer in base : base[answer] !== value) out[answer] = value;
  }
  return out;
}

/**
 * Normalise every override KEY at the save boundary (case, spaces), so a caller
 * may send "BYD Shark 6" and downstream comparison stays a plain equality on
 * normalised keys. ⚠️ Two keys that normalise to the same answer with
 * DIFFERENT values are reported, never silently resolved — which one wins
 * would be an accident of object key order.
 */
export function normalizeOverrideKeys(overrides: ModelMapOverrides): {
  overrides: ModelMapOverrides;
  conflicts: string[];
} {
  const out: ModelMapOverrides = {};
  const conflicts: string[] = [];
  for (const [answer, value] of Object.entries(overrides)) {
    const key = normalizeKey(answer);
    if (key === '') continue;
    if (key in out && out[key] !== value) {
      if (!conflicts.includes(key)) conflicts.push(key);
      continue;
    }
    out[key] = value;
  }
  return { overrides: out, conflicts };
}

export function overrideCount(overrides: ModelMapOverrides): number {
  return Object.keys(overrides).length;
}

export type ModelMapRowOrigin = 'standard' | 'overridden' | 'added';

export interface ModelMapRowView {
  answer: string;
  value: string;
  origin: ModelMapRowOrigin;
  /** What the standard maps this answer to — set for `overridden`, null otherwise. */
  standardValue: string | null;
}

export interface ModelMapView {
  rows: ModelMapRowView[];
  /** Standard rows this route removes, with what the standard would have sent. */
  removed: Array<{ answer: string; standardValue: string }>;
}

/** Every row of the effective map, labelled with where it came from. Sorted by answer. */
export function describeModelMapRows(
  standard: Record<string, string> | null,
  overrides: ModelMapOverrides,
): ModelMapView {
  const base = normalized(standard ?? {});
  const diff = normalized(overrides);
  const effective = effectiveModelMap(base, diff);
  const rows: ModelMapRowView[] = Object.entries(effective)
    .map(([answer, value]) => {
      if (!(answer in diff)) return { answer, value, origin: 'standard' as const, standardValue: null };
      if (answer in base) return { answer, value, origin: 'overridden' as const, standardValue: base[answer]! };
      return { answer, value, origin: 'added' as const, standardValue: null };
    })
    .sort((a, b) => a.answer.localeCompare(b.answer));
  const removed = Object.entries(diff)
    .filter(([answer, value]) => value === null && answer in base)
    .map(([answer]) => ({ answer, standardValue: base[answer]! }))
    .sort((a, b) => a.answer.localeCompare(b.answer));
  return { rows, removed };
}

/** Set one row. Setting it back to the standard's value REMOVES the override. */
export function setModelMapRow(
  standard: Record<string, string> | null,
  overrides: ModelMapOverrides,
  answer: string,
  value: string,
): ModelMapOverrides {
  const key = normalizeKey(answer);
  if (key === '') return { ...overrides };
  const base = normalized(standard ?? {});
  const next = { ...normalized(overrides) };
  if (base[key] === value) delete next[key];
  else next[key] = value;
  return next;
}

/** Remove one row: a standard row becomes a `null` override; an added row simply goes. */
export function removeModelMapRow(
  standard: Record<string, string> | null,
  overrides: ModelMapOverrides,
  answer: string,
): ModelMapOverrides {
  const key = normalizeKey(answer);
  const base = normalized(standard ?? {});
  const next = { ...normalized(overrides) };
  if (key in base) next[key] = null;
  else delete next[key];
  return next;
}

/** Put one row back to whatever the standard says (or drop it, if the standard lacks it). */
export function resetModelMapRow(overrides: ModelMapOverrides, answer: string): ModelMapOverrides {
  const next = { ...normalized(overrides) };
  delete next[normalizeKey(answer)];
  return next;
}
